import Button from "@mui/material/Button";
import { MainContainer, MainContent } from "../common/components/Layout";
import { useTranslation } from "react-i18next";
import { TenantsList, getTenantList } from "./AdminListApi";
import { callApi, handleErrorCommon } from "../common/api/Api";
import { useEffect, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { DataGrid, GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import { gridLang } from "../i18n/LangUtil";
import { contractTypeForView, useCategoryForView } from "../common/utils/Utils";
import { RegisterTenantButton } from "./RegisterTenantButton";
import { UpdateTenantButton } from "./UpdateTenantButton";
import { convertStateValue } from "./CompatibleCountry";
import { LoginUserComponent } from "./LoginUserComponent";

export const TenantListPage = (): JSX.Element => {
  const { t, i18n } = useTranslation();
  const [tenants, setTenants] = useState<TenantsList>();
  const { showBoundary } = useErrorBoundary();

  const refreshTenants = () => {
    (async () => {
      await callApi(async () => {
        const result = await getTenantList();
        if (result.error) {
          return handleErrorCommon(result.error?.response?.data, result.error, showBoundary, t);
        }
        if (!result.data || !result.data.results) {
          setTenants({} as TenantsList);
          return;
        }
        setTenants({ ...result.data });
      });
    })();
  };

  useEffect(() => {
    refreshTenants();
  }, []);

  const columns: GridColDef[] = [
    {
      field: "editButton",
      headerName: "",
      sortable: false,
      disableColumnMenu: true,
      width: 90,
      renderCell: (params) => {
        return (
          <UpdateTenantButton
            customerTenantId={params.row.id}
            contractorId={params.row.contractors[0].id}
            formdata={{
              companyName: params.row.customerName,
              useCategory: params.row.useCategory,
              country: params.row.country,
              state: convertStateValue(params.row.country, params.row.contractors[0].state),
              givenName: params.row.contractors[0].givenName,
              familyName: params.row.contractors[0].familyName,
              emailAddress: params.row.contractors[0].mailAddress,
              contractType: params.row.contractors[0].contractType,
            }}
            onAfterRegister={() => {
              refreshTenants();
            }}
          />
        );
      },
    },
    {
      field: "tenantId",
      headerName: t("common.tenantId"),
      sortable: true,
      disableColumnMenu: true,
      width: 150,
    },
    {
      field: "customerName",
      headerName: t("admin.companyName"),
      sortable: true,
      disableColumnMenu: true,
      width: 300,
    },
    {
      field: "useCategory",
      headerName: t("admin.useCategory"),
      sortable: true,
      disableColumnMenu: true,
      width: 100,
      valueGetter: (params: GridValueGetterParams) => `${useCategoryForView(params.row.useCategory, t)}`,
    },
    {
      field: "country",
      headerName: t("admin.country"),
      sortable: true,
      disableColumnMenu: true,
      width: 100,
    },
    {
      field: "name",
      headerName: t("common.owner"),
      sortable: true,
      disableColumnMenu: true,
      width: 250,
      valueGetter: (params: GridValueGetterParams) =>
        `${params.row.contractors[0].familyName || ""} ${params.row.contractors[0].givenName || ""}`,
    },
    {
      field: "mailAddress",
      headerName: t("common.emailAddress"),
      sortable: true,
      disableColumnMenu: true,
      width: 300,
      valueGetter: (params: GridValueGetterParams) => `${params.row.contractors[0].mailAddress}`,
    },
    {
      field: "contractType",
      headerName: t("admin.contractType"),
      sortable: true,
      disableColumnMenu: true,
      width: 200,
      valueGetter: (params: GridValueGetterParams) =>
        `${contractTypeForView(params.row.contractors[0].contractType, t)}`,
    },
  ];

  if (!tenants || !tenants.results) {
    return <></>;
  }

  return (
    <MainContainer>
      <MainContent className="flex flex-col gap-y-4">
        <LoginUserComponent />
        <div className="flex flex-col items-start">
          <Button variant="text" href="/admin/menu">
            {t("admin.backMenuButton")}
          </Button>
        </div>
        <div
          style={{
            width: "60rem",
            height: "4rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "left",
          }}
        >
          <RegisterTenantButton
            onAfterRegister={() => {
              refreshTenants();
            }}
          />
        </div>
        <div>
          <DataGrid
            rows={tenants.results}
            columns={columns}
            hideFooter
            localeText={gridLang(i18n).components.MuiDataGrid.defaultProps.localeText}
          />
        </div>
        <div className="flex flex-col items-start">
          <Button variant="text" href="/admin/menu">
            {t("admin.backMenuButton")}
          </Button>
        </div>
      </MainContent>
    </MainContainer>
  );
};
